/**
 * Property model interfaces for the admin UI and API
 */

import { BaseItem } from './common';
import { UrlItem } from './source';

// Type definitions
export type YesNo = 'yes' | '';

// Media item structure
export interface MediaItem {
  url: string;
  description?: string;
}

// Room structure
export interface Room {
  name: string;
  dimensions?: string;
  description?: string;
}

// Virtual tour structure
export interface VirtualTour {
  url: string;
  label?: string;
}

export interface PropertyListResponse {
  items: PropertyItem[];
  nextToken?: string;
}

export interface PropertyItem extends BaseItem {
  id: string;
  sourceId: string;
  sourceUrl: string;
  agencyId: string;
  // For list pages to identify the property
  title: string;
  notes?: string;
  // Note: Property active status is determined by gsi1pk === 'PROPERTY#ACTIVE'
  // The active field is not used - use gsi1pk instead

  fields: PropertyHiveFields;

  // Associated URL item for debugging and management
  urlItem?: UrlItem | null;

  // Token to access the property without api key or creds
  accessToken?: string;

  // Property locking - if locked, only partial parsing is allowed
  locked?: boolean;
}

export const PROPERTY_DEPARTMENT_LABELS: Record<string, string> = {
  'residential-sales': 'Residential Sales',
  'residential-lettings': 'Residential Lettings',
  commercial: 'Commercial',
};

export interface PropertyHiveFields {
  // Basic property details
  department?: string; // Stored as slug e.g. residential-sales
  propertyType?: string;
  bedrooms?: number;
  bathrooms?: number;
  receptionRooms?: number;

  // Address fields - directly matching PropertyHive structure
  addressNameNumber?: string;
  addressStreet?: string;
  addressTwo?: string;
  addressThree?: string;
  addressFour?: string;
  postcode?: string;
  city?: string;
  state?: string;
  country?: string;

  // Price/Rent fields
  price?: number;
  priceQualifier?: string;
  poa?: YesNo;
  currency?: string;
  rent?: number;
  rentFrequency?: string;

  // Marketing fields
  onMarket?: YesNo;
  featured?: YesNo;
  availability?: string;

  // Description fields
  title?: string;
  summary?: string;
  aiTextContent?: string;
  features?: string[];

  // Property attributes
  parking?: string | string[];
  outsideSpace?: string | string[];
  councilTax?: string;
  tenure?: string;
  saleBy?: string;
  yearBuilt?: number;

  // Media fields
  photos?: string[];
  floorplans?: string[];
  brochures?: string[];
  epcs?: string[];
  virtualTours?: VirtualTour[];

  // Room details
  rooms?: Room[];

  // Commercial property fields
  forSale?: YesNo;
  toRent?: YesNo;
  priceFrom?: number;
  priceTo?: number;
  priceUnits?: string;
  pricePoa?: YesNo;
  rentFrom?: number;
  rentTo?: number;
  rentUnits?: string;
  rentPoa?: YesNo;
  floorAreaFrom?: number;
  floorAreaTo?: number;
  floorAreaUnits?: string;
  siteAreaFrom?: number;
  siteAreaTo?: number;
  siteAreaUnits?: string;
  commercialPriceCurrency?: string;
  commercialRentCurrency?: string;

  // Residential lettings fields
  deposit?: number;
  availableDate?: string;
  furnished?: string;

  // Location fields
  location?: string;
  latitude?: number;
  longitude?: number;

  // Leasehold information - flat structure to match PropertyHive
  groundRent?: string;
  groundRentReviewYears?: string;
  serviceCharge?: string;
  leaseholdYearsRemaining?: string;
  sharedOwnership?: YesNo;
  sharedOwnershipPercentage?: string;
}

export interface FieldInfo {
  id: string;
  name: string;
  description: string;
  type?: 'text' | 'number' | 'valueList' | 'array' | 'complex'; // New type field
  multiple?: boolean; // is an array
  complex?: boolean; // is a complex object with nested properties
  objectProperties?: { key: string; label: string }[]; // properties for complex objects
  values?: Record<string, string>; // key-value store for valueList type fields
  defaultMappings?: Record<string, string>; // default patterns for each value
  options?: string[]; // valid options for this field
  required?: boolean;
}

interface ReapitValueOption {
  value: string;
  label: string;
  patterns: string[];
}

const toValueRecord = (
  options: ReapitValueOption[]
): Record<string, string> => {
  return options.reduce<Record<string, string>>((acc, option) => {
    acc[option.value] = option.label;
    return acc;
  }, {});
};

const toDefaultMappings = (
  options: ReapitValueOption[]
): Record<string, string> => {
  return options.reduce<Record<string, string>>((acc, option) => {
    const pattern = Array.from(
      new Set(option.patterns.map((entry) => entry.trim()).filter(Boolean))
    ).join('|');

    if (pattern) {
      acc[option.value] = pattern;
    }

    return acc;
  }, {});
};

const REAPIT_PROPERTY_TYPE_OPTIONS: ReapitValueOption[] = [
  {
    value: 'house - terraced',
    label: 'House - Terraced',
    patterns: [
      'terraced house',
      'terrace house',
      'mid terrace',
      'mid-terrace house',
    ],
  },
  {
    value: 'house - endTerrace',
    label: 'House - End Terrace',
    patterns: ['end of terrace house', 'end terrace', 'end-terrace house'],
  },
  {
    value: 'house - semiDetached',
    label: 'House - Semi-Detached',
    patterns: ['semi detached house', 'semi-detached house', 'semi det house'],
  },
  {
    value: 'house - linkDetached',
    label: 'House - Link Detached',
    patterns: ['link detached house', 'linked detached house', 'link-detached'],
  },
  {
    value: 'house - detached',
    label: 'House - Detached',
    patterns: ['detached house', '^detached$'],
  },
  {
    value: 'house - mews',
    label: 'House - Mews',
    patterns: ['mews house', 'mews property'],
  },
  {
    value: 'house',
    label: 'House',
    patterns: ['house'],
  },
  {
    value: 'bungalow - terraced',
    label: 'Bungalow - Terraced',
    patterns: ['terraced bungalow', 'terrace bungalow'],
  },
  {
    value: 'bungalow - endTerrace',
    label: 'Bungalow - End Terrace',
    patterns: ['end of terrace bungalow', 'end terrace bungalow'],
  },
  {
    value: 'bungalow - semiDetached',
    label: 'Bungalow - Semi-Detached',
    patterns: [
      'semi detached bungalow',
      'semi-detached bungalow',
      'semi det bungalow',
    ],
  },
  {
    value: 'bungalow - linkDetached',
    label: 'Bungalow - Link Detached',
    patterns: [
      'link detached bungalow',
      'linked detached bungalow',
      'link-detached bungalow',
    ],
  },
  {
    value: 'bungalow - detached',
    label: 'Bungalow - Detached',
    patterns: ['detached bungalow', '^detached bungalow$'],
  },
  {
    value: 'bungalow - mews',
    label: 'Bungalow - Mews',
    patterns: ['mews bungalow'],
  },
  {
    value: 'bungalow',
    label: 'Bungalow',
    patterns: ['bungalow'],
  },
  {
    value: 'flatApartment - basement',
    label: 'Flat/Apartment - Basement',
    patterns: ['basement flat', 'basement apartment'],
  },
  {
    value: 'flatApartment - lowerGroundFloor',
    label: 'Flat/Apartment - Lower Ground Floor',
    patterns: [
      'lower ground floor flat',
      'lower ground apartment',
      'garden level flat',
      'lower-ground floor flat',
      'lower-ground floor apartment',
    ],
  },
  {
    value: 'flatApartment - groundFloor',
    label: 'Flat/Apartment - Ground Floor',
    patterns: [
      'ground floor flat',
      'ground floor apartment',
      'ground-floor flat',
      'ground-floor apartment',
    ],
  },
  {
    value: 'flatApartment - firstFloor',
    label: 'Flat/Apartment - First Floor',
    patterns: [
      'first floor flat',
      'first floor apartment',
      'first-floor flat',
      'first-floor apartment',
    ],
  },
  {
    value: 'flatApartment - upperFloorWithLift',
    label: 'Flat/Apartment - Upper Floor With Lift',
    patterns: [
      'upper floor flat with lift',
      'upper-floor flat with lift',
      'upper floor apartment with lift',
      'upper-floor apartment with lift',
      'top floor flat with lift',
      'top-floor flat with lift',
      'top floor apartment with lift',
      'top-floor apartment with lift',
    ],
  },
  {
    value: 'flatApartment - upperFloor',
    label: 'Flat/Apartment - Upper Floor',
    patterns: [
      'upper floor flat',
      'upper floor apartment',
      'upper-floor flat',
      'upper-floor apartment',
      'top floor flat',
      'top floor apartment',
      'top-floor flat',
      'top-floor apartment',
    ],
  },
  {
    value: 'flatApartment - penthouse',
    label: 'Flat/Apartment - Penthouse',
    patterns: ['penthouse flat', 'penthouse apartment'],
  },
  {
    value: 'flatApartment',
    label: 'Flat/Apartment',
    patterns: ['flat', 'apartment'],
  },
  {
    value: 'maisonette - basement',
    label: 'Maisonette - Basement',
    patterns: ['basement maisonette'],
  },
  {
    value: 'maisonette - lowerGroundFloor',
    label: 'Maisonette - Lower Ground Floor',
    patterns: [
      'lower ground floor maisonette',
      'garden level maisonette',
      'lower-ground floor maisonette',
    ],
  },
  {
    value: 'maisonette - groundFloor',
    label: 'Maisonette - Ground Floor',
    patterns: ['ground floor maisonette', 'ground-floor maisonette'],
  },
  {
    value: 'maisonette - firstFloor',
    label: 'Maisonette - First Floor',
    patterns: ['first floor maisonette', 'first-floor maisonette'],
  },
  {
    value: 'maisonette - upperFloorWithLift',
    label: 'Maisonette - Upper Floor With Lift',
    patterns: [
      'upper floor maisonette with lift',
      'upper-floor maisonette with lift',
      'maisonette with lift',
    ],
  },
  {
    value: 'maisonette - upperFloor',
    label: 'Maisonette - Upper Floor',
    patterns: [
      'upper floor maisonette',
      'upper-floor maisonette',
      'top floor maisonette',
      'top-floor maisonette',
    ],
  },
  {
    value: 'maisonette - penthouse',
    label: 'Maisonette - Penthouse',
    patterns: ['penthouse maisonette'],
  },
  {
    value: 'maisonette',
    label: 'Maisonette',
    patterns: ['maisonette'],
  },
  {
    value: 'cottage - terraced',
    label: 'Cottage - Terraced',
    patterns: ['terraced cottage', 'terrace cottage'],
  },
  {
    value: 'cottage - endTerrace',
    label: 'Cottage - End Terrace',
    patterns: ['end terrace cottage', 'end of terrace cottage'],
  },
  {
    value: 'cottage - semiDetached',
    label: 'Cottage - Semi-Detached',
    patterns: [
      'semi detached cottage',
      'semi-detached cottage',
      'semi det cottage',
    ],
  },
  {
    value: 'cottage - linkDetached',
    label: 'Cottage - Link Detached',
    patterns: ['link detached cottage', 'linked detached cottage'],
  },
  {
    value: 'cottage - detached',
    label: 'Cottage - Detached',
    patterns: ['detached cottage'],
  },
  {
    value: 'cottage - mews',
    label: 'Cottage - Mews',
    patterns: ['mews cottage'],
  },
  {
    value: 'cottage',
    label: 'Cottage',
    patterns: ['cottage'],
  },
  {
    value: 'studio - basement',
    label: 'Studio - Basement',
    patterns: ['basement studio'],
  },
  {
    value: 'studio - lowerGroundFloor',
    label: 'Studio - Lower Ground Floor',
    patterns: [
      'lower ground floor studio',
      'garden level studio',
      'lower-ground floor studio',
    ],
  },
  {
    value: 'studio - groundFloor',
    label: 'Studio - Ground Floor',
    patterns: ['ground floor studio', 'ground-floor studio'],
  },
  {
    value: 'studio - firstFloor',
    label: 'Studio - First Floor',
    patterns: ['first floor studio', 'first-floor studio'],
  },
  {
    value: 'studio - upperFloorWithLift',
    label: 'Studio - Upper Floor With Lift',
    patterns: [
      'upper floor studio with lift',
      'upper-floor studio with lift',
      'studio with lift',
    ],
  },
  {
    value: 'studio - upperFloor',
    label: 'Studio - Upper Floor',
    patterns: [
      'upper floor studio',
      'upper-floor studio',
      'top floor studio',
      'top-floor studio',
    ],
  },
  {
    value: 'studio - penthouse',
    label: 'Studio - Penthouse',
    patterns: ['penthouse studio'],
  },
  {
    value: 'studio',
    label: 'Studio',
    patterns: ['studio'],
  },
  {
    value: 'townhouse - terraced',
    label: 'Townhouse - Terraced',
    patterns: [
      'terraced townhouse',
      'terrace townhouse',
      'mid terrace townhouse',
      'mid-terrace townhouse',
    ],
  },
  {
    value: 'townhouse - endTerrace',
    label: 'Townhouse - End Terrace',
    patterns: [
      'end terrace townhouse',
      'end of terrace townhouse',
      'end-terrace townhouse',
    ],
  },
  {
    value: 'townhouse - semiDetached',
    label: 'Townhouse - Semi-Detached',
    patterns: [
      'semi detached townhouse',
      'semi-detached townhouse',
      'semi det townhouse',
    ],
  },
  {
    value: 'townhouse - linkDetached',
    label: 'Townhouse - Link Detached',
    patterns: ['link detached townhouse', 'linked detached townhouse'],
  },
  {
    value: 'townhouse - detached',
    label: 'Townhouse - Detached',
    patterns: ['detached townhouse'],
  },
  {
    value: 'townhouse - mews',
    label: 'Townhouse - Mews',
    patterns: ['mews townhouse'],
  },
  {
    value: 'townhouse',
    label: 'Townhouse',
    patterns: ['townhouse', 'town house'],
  },
  {
    value: 'farm',
    label: 'Farm',
    patterns: ['farm', 'smallholding', 'equestrian'],
  },
  {
    value: 'developmentPlot',
    label: 'Development Plot',
    patterns: [
      'development plot',
      'development site',
      'building plot',
      'building site',
      'development land',
    ],
  },
  {
    value: 'land',
    label: 'Land',
    patterns: ['land', 'acreage', 'parcel of land'],
  },
];

const REAPIT_PRICE_QUALIFIER_OPTIONS: ReapitValueOption[] = [
  {
    value: 'priceOnApplication',
    label: 'Price On Application',
    patterns: ['price on application', 'price on appl', 'poa'],
  },
  {
    value: 'offersInExcess',
    label: 'Offers In Excess',
    patterns: ['offers in excess', 'oieo', 'in excess', 'in excess of'],
  },
  {
    value: 'offersInRegion',
    label: 'Offers In Region',
    patterns: ['offers in region', 'oir', 'region of'],
  },
  {
    value: 'offersOver',
    label: 'Offers Over',
    patterns: ['offers over', 'o/o'],
  },
  {
    value: 'guidePrice',
    label: 'Guide Price',
    patterns: ['guide price', 'price guide'],
  },
  {
    value: 'fixedPrice',
    label: 'Fixed Price',
    patterns: ['fixed price'],
  },
  {
    value: 'priceReducedTo',
    label: 'Price Reduced To',
    patterns: ['price reduced to', 'reduced to', 'reduced price'],
  },
  {
    value: 'askingPrice',
    label: 'Asking Price',
    patterns: ['asking price', '^asking$'],
  },
];

const REAPIT_TENURE_OPTIONS: ReapitValueOption[] = [
  {
    value: 'shareOfFreehold',
    label: 'Share of Freehold',
    patterns: ['share of freehold', 'shared freehold', ' sof '],
  },
  {
    value: 'freehold',
    label: 'Freehold',
    patterns: ['freehold', 'fh'],
  },
  {
    value: 'leasehold',
    label: 'Leasehold',
    patterns: ['leasehold', 'lh'],
  },
  {
    value: 'commonhold',
    label: 'Commonhold',
    patterns: ['commonhold', 'common hold'],
  },
  {
    value: 'tba',
    label: 'To Be Advised',
    patterns: [
      'tba',
      'to be advised',
      'to be confirmed',
      'tbc',
      'tbd',
      'unknown tenure',
    ],
  },
];

const REAPIT_FURNISHED_OPTIONS: ReapitValueOption[] = [
  {
    value: 'partFurnished',
    label: 'Part Furnished',
    patterns: [
      'part furnished',
      'part-furnished',
      'partially furnished',
      'semi furnished',
    ],
  },
  {
    value: 'furnished',
    label: 'Furnished',
    patterns: ['furnished', 'fully furnished'],
  },
  {
    value: 'unfurnished',
    label: 'Unfurnished',
    patterns: ['unfurnished', 'no furniture', 'not furnished'],
  },
];

const REAPIT_AVAILABILITY_OPTIONS: ReapitValueOption[] = [
  {
    value: 'preAppraisal',
    label: 'Pre-Appraisal',
    patterns: ['pre-appraisal', 'pre appraisal', 'preappraisal'],
  },
  {
    value: 'paidValuation',
    label: 'Paid Valuation',
    patterns: ['paid valuation'],
  },
  {
    value: 'valuation',
    label: 'Valuation',
    patterns: ['valuation', 'appraisal'],
  },
  {
    value: 'forSale',
    label: 'For Sale',
    patterns: ['for sale', 'sale', 'available for sale'],
  },
  {
    value: 'forSaleUnavailable',
    label: 'For Sale (Unavailable)',
    patterns: ['sale unavailable', 'sale not available', 'sale off market'],
  },
  {
    value: 'toLet',
    label: 'To Let',
    patterns: ['to let', 'to rent', 'available to let', 'available to rent'],
  },
  {
    value: 'toLetUnavailable',
    label: 'To Let (Unavailable)',
    patterns: ['to let unavailable', 'let unavailable', 'to rent unavailable'],
  },
  {
    value: 'underOffer',
    label: 'Under Offer',
    patterns: [
      'under offer',
      'sale agreed',
      'sold subject to contract',
      'stc',
      'let agreed',
    ],
  },
  {
    value: 'underOfferUnavailable',
    label: 'Under Offer (Unavailable)',
    patterns: ['under offer unavailable', 'under offer off market'],
  },
  {
    value: 'reserved',
    label: 'Reserved',
    patterns: ['reserved', 'reservation'],
  },
  {
    value: 'arrangingTenancy',
    label: 'Arranging Tenancy',
    patterns: ['arranging tenancy', 'arranging let', 'let agreed processing'],
  },
  {
    value: 'arrangingTenancyUnavailable',
    label: 'Arranging Tenancy (Unavailable)',
    patterns: ['arranging tenancy unavailable', 'arranging tenancy off market'],
  },
  {
    value: 'tenancyCurrent',
    label: 'Tenancy Current',
    patterns: ['tenancy current', 'currently let', 'tenancy ongoing'],
  },
  {
    value: 'tenancyCurrentUnavailable',
    label: 'Tenancy Current (Unavailable)',
    patterns: [
      'tenancy current unavailable',
      'currently let unavailable',
      'tenancy in place unavailable',
    ],
  },
  {
    value: 'tenancyFinished',
    label: 'Tenancy Finished',
    patterns: ['tenancy finished', 'let completed', 'tenancy ended'],
  },
  {
    value: 'tenancyCancelled',
    label: 'Tenancy Cancelled',
    patterns: ['tenancy cancelled', 'let cancelled', 'tenancy aborted'],
  },
  {
    value: 'exchanged',
    label: 'Exchanged',
    patterns: ['exchanged', 'contracts exchanged'],
  },
  {
    value: 'completed',
    label: 'Completed',
    patterns: ['completed', 'completion'],
  },
  {
    value: 'soldExternally',
    label: 'Sold Externally',
    patterns: ['sold externally', 'sold by other agent', 'sold elsewhere'],
  },
  {
    value: 'sold',
    label: 'Sold',
    patterns: ['sold', 'sale complete'],
  },
  {
    value: 'letByOtherAgent',
    label: 'Let By Other Agent',
    patterns: ['let by other agent', 'let elsewhere'],
  },
  {
    value: 'letPrivately',
    label: 'Let Privately',
    patterns: ['let privately', 'let by landlord'],
  },
  {
    value: 'provisional',
    label: 'Provisional',
    patterns: ['provisional', 'awaiting confirmation'],
  },
  {
    value: 'withdrawn',
    label: 'Withdrawn',
    patterns: ['withdrawn', 'off market', 'withdrawn from market'],
  },
];

const REAPIT_OUTSIDE_SPACE_OPTIONS: ReapitValueOption[] = [
  {
    value: 'roofTerrace',
    label: 'Roof Terrace',
    patterns: ['roof terrace', 'roof deck', 'roof garden'],
  },
  {
    value: 'communalGardens',
    label: 'Communal Gardens',
    patterns: [
      'communal garden',
      'communal gardens',
      'shared garden',
      'communal grounds',
    ],
  },
  {
    value: 'balcony',
    label: 'Balcony',
    patterns: ['balcony', 'balconies'],
  },
  {
    value: 'patio',
    label: 'Patio',
    patterns: ['patio'],
  },
  {
    value: 'conservatory',
    label: 'Conservatory',
    patterns: ['conservatory', 'sun room', 'sunroom', 'orangery'],
  },
  {
    value: 'garden',
    label: 'Garden',
    patterns: ['garden', 'rear garden', 'front garden', 'private garden'],
  },
  {
    value: 'land',
    label: 'Land',
    patterns: ['land', 'grounds', 'acreage', 'paddock'],
  },
];

const REAPIT_PARKING_OPTIONS: ReapitValueOption[] = [
  {
    value: 'tripleGarage',
    label: 'Triple Garage',
    patterns: ['triple garage', 'three car garage'],
  },
  {
    value: 'doubleGarage',
    label: 'Double Garage',
    patterns: ['double garage', 'two car garage'],
  },
  {
    value: 'underground',
    label: 'Underground',
    patterns: [
      'underground parking',
      'basement parking',
      'undercroft parking',
      'underground garage',
      'undercroft garage',
    ],
  },
  {
    value: 'garage',
    label: 'Garage',
    patterns: ['garage', 'single garage', 'garage parking'],
  },
  {
    value: 'secure',
    label: 'Secure',
    patterns: [
      'secure parking',
      'secure car park',
      'gated parking',
      'secure garage',
    ],
  },
  {
    value: 'offStreet',
    label: 'Off Street',
    patterns: [
      'off street parking',
      'off-street parking',
      'off road parking',
      'driveway parking',
      'allocated parking',
      'private parking',
    ],
  },
  {
    value: 'residents',
    label: 'Residents',
    patterns: [
      'residents parking',
      'resident permit',
      'resident parking',
      'permit parking',
    ],
  },
];

// Property fields that align with propertyhive
export const PROPERTY_FIELDS: FieldInfo[] = [
  // Basic property details
  {
    id: 'department',
    name: 'Department',
    description: 'Property department (Sales, Lettings, Commercial)',
    type: 'valueList',
    values: PROPERTY_DEPARTMENT_LABELS,
    defaultMappings: {
      'residential-sales': 'sale|sales|for sale|buy',
      'residential-lettings': 'rent|rental|let|letting|to let|to rent',
      commercial: 'commercial|business|office|retail|industrial',
    },
  },
  {
    id: 'propertyType',
    name: 'Property Type',
    description: 'Type of property',
    type: 'valueList',
    values: toValueRecord(REAPIT_PROPERTY_TYPE_OPTIONS),
    defaultMappings: toDefaultMappings(REAPIT_PROPERTY_TYPE_OPTIONS),
  },
  {
    id: 'bedrooms',
    name: 'Bedrooms',
    description: 'Number of bedrooms',
    type: 'number',
  },
  {
    id: 'bathrooms',
    name: 'Bathrooms',
    description: 'Number of bathrooms',
    type: 'number',
  },
  {
    id: 'receptionRooms',
    name: 'Reception Rooms',
    description: 'Number of reception rooms',
    type: 'number',
  },

  // Address fields
  {
    id: 'addressNameNumber',
    name: 'Address Name/Number',
    description: 'House name or number',
  },
  { id: 'addressStreet', name: 'Street', description: 'Street name' },
  {
    id: 'addressTwo',
    name: 'Address Line 2',
    description: 'Second line of address',
  },
  {
    id: 'addressThree',
    name: 'Address Line 3',
    description: 'Third line of address',
  },
  {
    id: 'addressFour',
    name: 'Address Line 4',
    description: 'Fourth line of address',
  },
  { id: 'postcode', name: 'Postcode', description: 'Postal/zip code' },
  { id: 'city', name: 'City', description: 'City location' },
  { id: 'state', name: 'State', description: 'State/province location' },
  // { id: 'country', name: 'Country', description: 'Country location' },

  // Price/Rent fields
  { id: 'price', name: 'Price', description: 'Property price', type: 'number' },
  {
    id: 'priceQualifier',
    name: 'Price Qualifier',
    description: 'Price qualifier (offers_over, etc)',
    type: 'valueList',
    values: toValueRecord(REAPIT_PRICE_QUALIFIER_OPTIONS),
    defaultMappings: toDefaultMappings(REAPIT_PRICE_QUALIFIER_OPTIONS),
  },
  // {
  //   id: 'poa',
  //   name: 'POA',
  //   description: 'Price on application (yes/no)',
  //   type: 'valueList',
  //   values: { yes: 'Yes', '': 'No' },
  //   defaultMappings: {
  //     yes: 'poa|price on application|price on appl|on application',
  //     '': '.*',
  //   },
  // },
  // {
  //   id: 'currency',
  //   name: 'Currency',
  //   description: 'Currency for price/rent',
  //   type: 'valueList',
  //   values: {
  //     GBP: 'GBP',
  //     EUR: 'EUR',
  //     USD: 'USD',
  //   },
  //   defaultMappings: {
  //     GBP: '£|gbp|pounds|sterling',
  //     EUR: '€|eur|euro|euros',
  //     USD: '\\$|usd|dollars',
  //   },
  // },
  // { id: 'rent', name: 'Rent', description: 'Rent amount', type: 'number' },
  // {
  //   id: 'rentFrequency',
  //   name: 'Rent Frequency',
  //   description: 'Rent frequency (pw, pcm, pq, pa)',
  //   type: 'valueList',
  //   values: {
  //     pw: 'Per Week',
  //     pcm: 'Per Calendar Month',
  //     pq: 'Per Quarter',
  //     pa: 'Per Annum',
  //   },
  //   defaultMappings: {
  //     pw: 'pw|per week|weekly|week',
  //     pcm: 'pcm|per calendar month|monthly|month',
  //     pq: 'pq|per quarter|quarterly|quarter',
  //     pa: 'pa|per annum|annually|yearly|year',
  //   },
  // },

  // Marketing fields
  // {
  //   id: 'onMarket',
  //   name: 'On Market',
  //   description: 'Whether property is on market (yes/no)',
  //   type: 'valueList',
  //   values: { yes: 'Yes', '': 'No' },
  //   defaultMappings: {
  //     yes: 'on market|available|for sale|to let|to rent',
  //     '': '.*',
  //   },
  // },
  // {
  //   id: 'featured',
  //   name: 'Featured',
  //   description: 'Whether property is featured (yes/no)',
  //   type: 'valueList',
  //   values: { yes: 'Yes', '': 'No' },
  //   defaultMappings: {
  //     yes: 'featured|premium|highlighted|showcase',
  //     '': '.*',
  //   },
  // },
  {
    id: 'availability',
    name: 'Availability',
    description: 'Property availability status',
    type: 'valueList',
    values: toValueRecord(REAPIT_AVAILABILITY_OPTIONS),
    defaultMappings: toDefaultMappings(REAPIT_AVAILABILITY_OPTIONS),
  },

  // Description fields
  {
    id: 'title',
    name: 'Title',
    description: 'Property title/headline',
    type: 'text',
  },
  {
    id: 'summary',
    name: 'Summary',
    description: 'Property summary',
    type: 'text',
  },
  {
    id: 'aiTextContent',
    name: 'AI Text Content',
    description: 'Raw text used for AI-assisted field extraction',
    type: 'text',
  },
  {
    id: 'features',
    name: 'Features',
    description: 'Property features/amenities',
    type: 'array',
    multiple: true,
  },

  // Property attributes
  {
    id: 'parking',
    name: 'Parking',
    description: 'Parking information',
    type: 'array',
    multiple: true,
    values: toValueRecord(REAPIT_PARKING_OPTIONS),
    defaultMappings: toDefaultMappings(REAPIT_PARKING_OPTIONS),
  },
  {
    id: 'outsideSpace',
    name: 'Outside Space',
    description: 'Outside space information',
    type: 'array',
    multiple: true,
    values: toValueRecord(REAPIT_OUTSIDE_SPACE_OPTIONS),
    defaultMappings: toDefaultMappings(REAPIT_OUTSIDE_SPACE_OPTIONS),
  },
  {
    id: 'councilTax',
    name: 'Council Tax Band',
    description: 'Council tax band',
    type: 'valueList',
    values: {
      A: 'A',
      B: 'B',
      C: 'C',
      D: 'D',
      E: 'E',
      F: 'F',
      G: 'G',
      H: 'H',
    },
    defaultMappings: {
      A: 'band a|a',
      B: 'band b|b',
      C: 'band c|c',
      D: 'band d|d',
      E: 'band e|e',
      F: 'band f|f',
      G: 'band g|g',
      H: 'band h|h',
    },
  },
  {
    id: 'tenure',
    name: 'Tenure',
    description: 'Tenure (freehold, leasehold)',
    type: 'valueList',
    values: toValueRecord(REAPIT_TENURE_OPTIONS),
    defaultMappings: toDefaultMappings(REAPIT_TENURE_OPTIONS),
  },
  {
    id: 'saleBy',
    name: 'Sale By',
    description: 'Sale method',
    type: 'valueList',
    values: {
      'Private Treaty': 'Private Treaty',
      Auction: 'Auction',
      Tender: 'Tender',
    },
    defaultMappings: {
      'Private Treaty': 'private treaty|private sale',
      Auction: 'auction|auction sale',
      Tender: 'tender|tender sale',
    },
  },
  {
    id: 'yearBuilt',
    name: 'Year Built',
    description: 'Year property was constructed',
    type: 'number',
  },

  // Media fields
  {
    id: 'photos',
    name: 'Photos',
    description: 'Property photos',
    type: 'array',
    multiple: true,
  },
  {
    id: 'floorplans',
    name: 'Floor Plans',
    description: 'Floor plan images',
    type: 'array',
    multiple: true,
  },
  {
    id: 'brochures',
    name: 'Brochures',
    description: 'Property brochures',
    type: 'array',
    multiple: true,
  },
  {
    id: 'epcs',
    name: 'EPCs',
    description: 'Energy Performance Certificates',
    type: 'array',
    multiple: true,
  },
  {
    id: 'virtualTours',
    name: 'Virtual Tours',
    description: 'Virtual tour links',
    type: 'complex',
    multiple: true,
    objectProperties: [
      { key: 'url', label: 'URL' },
      { key: 'label', label: 'Label' },
    ],
  },

  // Room details
  // {
  //   id: 'rooms',
  //   name: 'Rooms',
  //   description: 'Room details',
  //   type: 'complex',
  //   multiple: true,
  //   objectProperties: [
  //     { key: 'name', label: 'Name' },
  //     { key: 'dimensions', label: 'Dimensions' },
  //     { key: 'description', label: 'Description' },
  //   ],
  // },

  // Commercial property fields
  // {
  //   id: 'forSale',
  //   name: 'For Sale',
  //   description: 'Commercial property for sale (yes/no)',
  //   type: 'valueList',
  //   values: { yes: 'Yes', '': 'No' },
  //   defaultMappings: {
  //     yes: 'for sale|sale|buy',
  //     '': '.*',
  //   },
  // },
  // {
  //   id: 'toRent',
  //   name: 'To Rent',
  //   description: 'Commercial property to rent (yes/no)',
  //   type: 'valueList',
  //   values: { yes: 'Yes', '': 'No' },
  //   defaultMappings: {
  //     yes: 'to rent|rent|let|letting',
  //     '': '.*',
  //   },
  // },
  // {
  //   id: 'priceFrom',
  //   name: 'Price From',
  //   description: 'Commercial minimum price',
  //   type: 'number',
  // },
  // {
  //   id: 'priceTo',
  //   name: 'Price To',
  //   description: 'Commercial maximum price',
  //   type: 'number',
  // },
  // {
  //   id: 'priceUnits',
  //   name: 'Price Units',
  //   description: 'Commercial price units',
  //   type: 'text',
  // },
  // {
  //   id: 'pricePoa',
  //   name: 'Price POA',
  //   description: 'Commercial price on application (yes/no)',
  //   type: 'valueList',
  //   values: { yes: 'Yes', '': 'No' },
  //   defaultMappings: {
  //     yes: 'poa|price on application|price on appl|on application',
  //     '': '.*',
  //   },
  // },
  // {
  //   id: 'rentFrom',
  //   name: 'Rent From',
  //   description: 'Commercial minimum rent',
  //   type: 'number',
  // },
  // {
  //   id: 'rentTo',
  //   name: 'Rent To',
  //   description: 'Commercial maximum rent',
  //   type: 'number',
  // },
  // {
  //   id: 'rentUnits',
  //   name: 'Rent Units',
  //   description: 'Commercial rent units',
  //   type: 'text',
  // },
  // {
  //   id: 'rentPoa',
  //   name: 'Rent POA',
  //   description: 'Commercial rent on application (yes/no)',
  //   type: 'valueList',
  //   values: { yes: 'Yes', '': 'No' },
  //   defaultMappings: {
  //     yes: 'poa|rent on application|rent on appl|on application',
  //     '': '.*',
  //   },
  // },
  // {
  //   id: 'floorAreaFrom',
  //   name: 'Floor Area From',
  //   description: 'Commercial minimum floor area',
  //   type: 'number',
  // },
  // {
  //   id: 'floorAreaTo',
  //   name: 'Floor Area To',
  //   description: 'Commercial maximum floor area',
  //   type: 'number',
  // },
  // {
  //   id: 'floorAreaUnits',
  //   name: 'Floor Area Units',
  //   description: 'Commercial floor area units',
  //   type: 'text',
  // },
  // {
  //   id: 'siteAreaFrom',
  //   name: 'Site Area From',
  //   description: 'Commercial minimum site area',
  //   type: 'number',
  // },
  // {
  //   id: 'siteAreaTo',
  //   name: 'Site Area To',
  //   description: 'Commercial maximum site area',
  //   type: 'number',
  // },
  // {
  //   id: 'siteAreaUnits',
  //   name: 'Site Area Units',
  //   description: 'Commercial site area units',
  //   type: 'text',
  // },
  // {
  //   id: 'commercialPriceCurrency',
  //   name: 'Commercial Price Currency',
  //   description: 'Currency for commercial price',
  //   type: 'valueList',
  //   values: {
  //     GBP: 'GBP',
  //     EUR: 'EUR',
  //     USD: 'USD',
  //   },
  //   defaultMappings: {
  //     GBP: '£|gbp|pounds|sterling',
  //     EUR: '€|eur|euro|euros',
  //     USD: '\\$|usd|dollars',
  //   },
  // },
  // {
  //   id: 'commercialRentCurrency',
  //   name: 'Commercial Rent Currency',
  //   description: 'Currency for commercial rent',
  //   type: 'valueList',
  //   values: {
  //     GBP: 'GBP',
  //     EUR: 'EUR',
  //     USD: 'USD',
  //   },
  //   defaultMappings: {
  //     GBP: '£|gbp|pounds|sterling',
  //     EUR: '€|eur|euro|euros',
  //     USD: '\\$|usd|dollars',
  //   },
  // },

  // Residential lettings fields
  // {
  //   id: 'deposit',
  //   name: 'Deposit',
  //   description: 'Rental deposit amount',
  //   type: 'number',
  // },
  // {
  //   id: 'availableDate',
  //   name: 'Available Date',
  //   description: 'Date property is available from',
  //   type: 'text',
  // },
  // {
  //   id: 'furnished',
  //   name: 'Furnished',
  //   description: 'Furnished status',
  //   type: 'valueList',
  //   values: toValueRecord(REAPIT_FURNISHED_OPTIONS),
  //   defaultMappings: toDefaultMappings(REAPIT_FURNISHED_OPTIONS),
  // },

  // Location fields
  {
    id: 'location',
    name: 'Location',
    description: 'Location taxonomy',
    type: 'text',
  },
  {
    id: 'latitude',
    name: 'Latitude',
    description: 'Property geo location latitude',
    type: 'number',
  },
  {
    id: 'longitude',
    name: 'Longitude',
    description: 'Property geo location longitude',
    type: 'number',
  },

  // Leasehold information
  // {
  //   id: 'groundRent',
  //   name: 'Ground Rent',
  //   description: 'Ground rent amount',
  //   type: 'text',
  // },
  // {
  //   id: 'groundRentReviewYears',
  //   name: 'Ground Rent Review Years',
  //   description: 'Years between ground rent reviews',
  //   type: 'text',
  // },
  // {
  //   id: 'serviceCharge',
  //   name: 'Service Charge',
  //   description: 'Service charge amount',
  //   type: 'text',
  // },
  // {
  //   id: 'leaseholdYearsRemaining',
  //   name: 'Leasehold Years Remaining',
  //   description: 'Years remaining on lease',
  //   type: 'text',
  // },
  // {
  //   id: 'sharedOwnership',
  //   name: 'Shared Ownership',
  //   description: 'Whether property is shared ownership',
  //   type: 'valueList',
  //   values: { yes: 'Yes', '': 'No' },
  //   defaultMappings: {
  //     yes: 'shared ownership|shared own|part buy part rent',
  //     '': '.*',
  //   },
  // },
  // {
  //   id: 'sharedOwnershipPercentage',
  //   name: 'Shared Ownership Percentage',
  //   description: 'Percentage of shared ownership',
  //   type: 'text',
  // },
];

export function createPropertyKey(
  id: string,
  sourceId: string
): { pk: string; sk: string } {
  return {
    pk: `SOURCE#${sourceId}`,
    sk: `PROPERTY#${id}`,
  };
}
